import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn, HttpEvent, HttpXsrfTokenExtractor } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, throwError, BehaviorSubject, EMPTY } from 'rxjs'; // 1. Added EMPTY here
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { ApiCallServiceService } from '../service/api_call_service/api-call-service.service';
import { DataStoreService } from '../utils/data_store/data-store.service';

let isRefreshing = false;
let isNavigatingToLogin = false; 
const refreshTokenSubject = new BehaviorSubject<boolean | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const apiService = inject(ApiCallServiceService);
  const store = inject(DataStoreService);
  
  const tokenExtractor = inject(HttpXsrfTokenExtractor);
  const xsrfToken = tokenExtractor.getToken();

  const authHeaderValue = store.getAuth();

  let securedReq = req.clone({
    withCredentials: true
  });

  if (authHeaderValue) {
    securedReq = securedReq.clone({
      headers: securedReq.headers.set('Authorization', authHeaderValue)
    });
  }

  const mutatingMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];
  if (xsrfToken && mutatingMethods.includes(securedReq.method.toUpperCase())) {
    securedReq = securedReq.clone({
      headers: securedReq.headers.set('X-XSRF-TOKEN', xsrfToken)
    });
  }

  return next(securedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // 2. SILENT EXIT SAFETY: If we are already logging out, swallow ALL remaining API requests completely!
      if (isNavigatingToLogin) {
        return EMPTY; // Destroys the request stream so components can never show an alert
      }

      let errorMessage = 'Something went wrong!!';

      if (error.error instanceof ErrorEvent) {
        return throwError(() => error.error.message);
      }

      // 3. This block handles when the actual /refreshtoken call fails
      if (securedReq.url.toLowerCase().includes('/refreshtoken')) {
        isNavigatingToLogin = true; 
        isRefreshing = false;       
        
        alert('Your session has expired or another device has logged in with your account!');
        
        store.clearAll();
        router.navigate(['/login']).then(() => {
          isNavigatingToLogin = false; 
        });
        
        return EMPTY; // Kill the master refresh error stream right here
      }

      if (securedReq.url.toLowerCase().includes('/login')) {
        const payload = error.error;
        if (payload?.Object?.includes('Unauthorized')) {
          errorMessage = 'Your password is incorrect!';
        } else {
          errorMessage = payload?.Object || payload?.Message || 'Authentication failed.';
        }
        return throwError(() => errorMessage);
      }

      // 4. Trigger the silent cookie-refresh routine on 401 or CORS status 0
      if (error.status === 401 || error.status === 0) {
        return handle401RetryLoop(securedReq, next, apiService);
      }

      // 5. Standard App Parsing Notification Matrix
      const errorData = error.error;
      if (errorData instanceof Blob) {
        errorMessage = 'Data Not Found!!';
      } else if (typeof errorData === 'string') {
        try {
          const parsedJson = JSON.parse(errorData);
          errorMessage = parsedJson?.Object || 'String evaluation discrepancy.';
        } catch {
          errorMessage = errorData;
        }
      } else if (errorData) {
        errorMessage = errorData.Message || errorData.Object || errorData.Status || errorMessage;
      }

      return throwError(() => errorMessage);
    })
  );
};

const handle401RetryLoop = (
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  apiService: ApiCallServiceService
): Observable<HttpEvent<any>> => {

  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return apiService.refreshTokenApiCall().pipe(
      switchMap(() => {
        isRefreshing = false;
        refreshTokenSubject.next(true);
        return next(request);
      }),
      catchError((err) => {
        isRefreshing = false;
        refreshTokenSubject.next(false); 
        return EMPTY; // 6. If refresh fails, return EMPTY to silence parallel requests waiting in line
      })
    );
  }

  return refreshTokenSubject.pipe(
    filter((result) => result !== null),
    take(1),
    switchMap((success) => {
      if (success) {
        return next(request);
      }
      return EMPTY; // 7. Silently drop parallel requests if the master token refresh failed
    })
  );
};
